import { useState } from "react";
import { CalendarDays } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * A date input that looks like one on every platform.
 *
 * ⚠️ **`<input type="date">` does not agree with itself across browsers, and the disagreement
 * is invisible on a desk.** Chrome, Edge and Firefox draw an empty date field as
 * `mm/dd/yyyy` with a calendar button beside it. **iOS Safari draws an empty one as nothing
 * at all** — no format hint, no icon, an empty box the same shape as a disabled field. The
 * control still works: tapping it opens the picker. Nothing on screen says so, and a filter
 * nobody can tell is a filter is one nobody uses.
 *
 * So both halves are drawn here rather than left to the platform:
 *
 * - **The icon is ours**, always present. The native indicator is faded out rather than
 *   removed, so it keeps its hit target where it already was and a desktop click on the icon
 *   still opens the calendar — we replace what it looks like and not what it does.
 * - **The format hint is ours too, and the native one is hidden while the field is empty**
 *   by making the input's own text transparent. Otherwise the two would sit on top of each
 *   other on Chrome and only there. It is removed the moment a date exists, so the value is
 *   drawn by the browser in the reader's own locale — which is the one part of a date field
 *   that should never be reimplemented.
 *
 * ⚠️ The hint is `pointer-events-none`, or it would swallow the tap it exists to invite.
 */
export function DateField({
  className,
  value,
  onFocus,
  onBlur,
  ...props
}: Omit<React.ComponentProps<"input">, "type">) {
  /**
   * ⚠️ Focus is tracked because a date input's `value` stays EMPTY until every part of the
   * date is valid. Somebody typing `08` into it on a desktop has changed nothing this
   * component can see — so hiding the native text on emptiness alone would blank the digits
   * as they were typed, and the field would appear not to accept input at all. The hint and
   * the transparency both lift the moment the field is focused, which hands it straight back
   * to the browser for the one job the browser does best.
   */
  const [focused, setFocused] = useState(false);

  const empty = value === undefined || value === null || value === "";
  const showHint = empty && !focused;

  return (
    <div className="relative">
      <input
        {...props}
        type="date"
        value={value}
        onFocus={(event) => {
          setFocused(true);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setFocused(false);
          onBlur?.(event);
        }}
        className={cn(
          className,
          // Room for the icon, so a long value never runs under it.
          "pr-9",
          // ⚠️ Hides the browser's own `mm/dd/yyyy` so it cannot double up with the hint
          // below. Never applied once there is a date to read, or while it is being typed.
          showHint && "text-transparent",
          // The native button keeps working and stops being drawn twice.
          "[&::-webkit-calendar-picker-indicator]:opacity-0",
        )}
      />

      {showHint && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-2 flex items-center text-sm text-muted-foreground"
        >
          {/* ⚠️ Not a format like `yyyy-mm-dd`. The browser renders a real value in the
              READER'S locale — `08/21/2026` here, `21/08/2026` elsewhere — so a format hint
              would contradict the field the moment somebody filled it in. What is worth
              saying is what an empty bound MEANS, which is that it is not narrowing anything. */}
          Any date
        </span>
      )}

      <CalendarDays
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-2.5 my-auto h-4 w-4 text-muted-foreground"
      />
    </div>
  );
}
